/**
 * Where to send someone after they sign in.
 *
 * The sign-in page accepts `?returnUrl=` so a deep link (a report a colleague
 * shared, a page the session expired on) survives the round trip. Only paths
 * inside the console are accepted: anything else — another site, a protocol-
 * relative `//host`, a backslash trick, the sign-in page itself — falls back
 * to the dashboard, so the parameter can never be used as an open redirect.
 */

export const DEFAULT_LANDING_URL = '/organization/dashboard';

/** Query parameter names shared by the guards, the sign-in form and logout. */
export const RETURN_URL_PARAM = 'returnUrl';
export const SIGN_IN_REASON_PARAM = 'reason';

/** Why the sign-in page is showing: drives the notice above the form. */
export type SignInReason = 'expired' | 'signed-out';

export function isSignInReason(value: unknown): value is SignInReason {
  return value === 'expired' || value === 'signed-out';
}

/** True for an internal console path such as `/organization/messages/12?tab=x`. */
export function isSafeReturnUrl(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const url = value.trim();
  if (url.length === 0 || url.length > 2048) return false;
  // Only plain absolute paths; no scheme, no `//host`, no backslashes or
  // control characters that some browsers normalise into a host.
  if (!url.startsWith('/') || url.startsWith('//')) return false;
  if (/[\\\u0000-\u001f\u007f]/.test(url)) return false;
  return /^\/organization(?:[/?#]|$)/.test(url);
}

/** The validated return URL, or the dashboard. */
export function safeReturnUrl(value: unknown): string {
  return isSafeReturnUrl(value) ? value.trim() : DEFAULT_LANDING_URL;
}

/**
 * The returnUrl worth carrying to the sign-in page: dropped when it is the
 * dashboard anyway, so the address bar stays clean in the common case.
 */
export function returnUrlParam(value: unknown): string | null {
  if (!isSafeReturnUrl(value)) return null;
  const url = value.trim();
  return url === DEFAULT_LANDING_URL || url === '/organization' ? null : url;
}
